<template>
  <div>
    <v-container class="rs-controls">
      <v-row>
        <v-col cols="8">
          <v-select
            v-model="sensorTypeName"
            item-text="typeName"
            :items="Object.values(sensorTypeList)"
            label="Sensortyp"
            v-on:change="setNewSensorType()"
          />
        </v-col>
        <v-col cols="4">
          <v-select
            v-model="subAddress"
            :items="[
              '0', '1',  '2', '3', '4', '5', '6', '7',
              '8', '9', '10', '11', '12', '13', '14', '15']"
            label="SubIndex"
            v-on:change="setNewSensorType()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="8">
          <v-row dense>
            <v-col cols="3" v-for="(button, index) in buttons" :key="`button-${button.name}-${index}`">
              <SensorButton
                v-model="button.value"
                :conversion="button.conversion"
                :name="button.name"
                :initialValue="button.initialValue"
                @input="onSensorInput(button, button.platformIndex == null ? index : button.platformIndex, $event)"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="4">
          <v-row dense>
            <v-col cols="4" v-for="(output, index) in outputs" :key="`output-${index}`">
              <Output
                :value="output.value"
                :conversion="output.conversion"
                :name="output.name"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { Bridge } from '@/bridges/Bridge.js';
import SensorButton from '@/components/SensorButton';
import Output from '@/components/Output';

const Connection = require('resol-vbus/src/connection');
const Telegram = require('resol-vbus/src/telegram');
const DdsInfo = require('@/helpers/DdsInfo');

class DdsBridge extends Bridge {
  constructor(options) {
    super();
    this.sensorType = options.sensorType || null;
    this.subaddress = options.subaddress || 0;
    this.buttons = options.buttons;
    this.outputs = options.outputs;
    this.connection = new Connection();
    this.connection.on('telegram', this.handleTelegram.bind(this));
  }

  sendUartData(index, rxBuffer) {
    if (index === 0) {
      this.connection.write(rxBuffer);
    }
  }

  handleTelegram(telegram) {
    if (!this.sensorType) {
      return;
    }
    const vbusAddress = this.sensorType.address + this.subaddress;
    if (telegram.destinationAddress !== vbusAddress
      && telegram.destinationAddress !== (vbusAddress & 0xFFF0)
      && telegram.destinationAddress !== 0x2000
    ) {
      return;
    }

    if (telegram.command === DdsInfo.DdsCommand.GETDATA.value) {
      const txFrameData = Buffer.alloc(21);
      let maxIndex = 0;
      for (let i = 0; i < this.buttons.length; i += 1) {
        const value = this.buttons[i].value.value || 0;
        const factor = this.sensorType.fields[i].type.factor || 100;
        maxIndex += this.sensorType.fields[i].byteSize;
        if (this.sensorType.fields[i].byteSize === 1) {
          txFrameData.writeUInt8(value * factor, this.sensorType.fields[i].byteIndex);
        } else if (this.sensorType.fields[i].byteSize === 2) {
          txFrameData.writeUInt16LE(value * factor, this.sensorType.fields[i].byteIndex);
        } else if (this.sensorType.fields[i].byteSize === 4) {
          txFrameData.writeUInt32LE(value * factor, this.sensorType.fields[i].byteIndex);
        }
      }

      const frameCount = +((maxIndex + 6) / 7).toFixed(0);
      const command = DdsInfo.DdsCommand.GETDATA_ACK.value + (frameCount << 5);

      const txTelegram = new Telegram({
        destinationAddress: telegram.sourceAddress,
        sourceAddress: vbusAddress,
        command,
        frameCount,
        frameData: txFrameData,
      });

      const txBuffer = txTelegram.toLiveBuffer();
      for (let i = 0; i < txBuffer.length; i += 1) {
        this.emit('uartDidTransmit', 0, txBuffer[i]);
      }
    }
  }

  setSensorTypeAndSubaddress(type, subaddress) {
    this.sensorType = type;
    this.subaddress = subaddress;
  }
}

export default {
  props: [
    'allowedSensorTypes',
    'deniedSensorTypes',
    'defaultSensorType',
    'defaultValues',
    'defaultSubAddress',
  ],

  components: {
    SensorButton,
    Output,
  },

  data: () => ({
    DdsInfo,
    sensorType: null,
    sensorTypeName: '',
    subAddress: '0',
    bridge: null,
    intervalId: 0,
    relaisState: [],

    buttons: [
    ],
    outputs: [
    ],

    uarts: [
      { name: 'UART0', kind: 'VBus', label: 'DDS' },
    ],
  }),

  computed: {
    sensorTypeList() {
      return Object.values(DdsInfo.DdsSensorTypes).filter((type) => {
        return (!this.deniedSensorTypes || this.deniedSensorTypes.indexOf(type.typeName) < 0)
          && (!this.allowedSensorTypes || this.allowedSensorTypes.indexOf(type.typeName) >= 0);
      });
    },
  },

  methods: {
    async prepareDevice() {
      if (this.defaultSubAddress) {
        this.$set(this, 'subAddress', this.defaultSubAddress);
      }
      this.$set(this, 'sensorTypeName', this.defaultSensorType);
      this.bridge = new DdsBridge({
        buttons: this.buttons,
        outputs: this.outputs,
      });
      this.setNewSensorType();
    },

    startDevice() {},

    restartDevice() {},

    onSensorInput(/*button, index, value*/) {},

    setNewSensorType() {
      const type = Object.values(DdsInfo.DdsSensorTypes).find(
        x => x.typeName === this.sensorTypeName,
      );
      if (type) {
        const typeChanged = type !== this.sensorType;
        this.$set(this, 'sensorType', type);
        this.bridge.setSensorTypeAndSubaddress(type, +this.subAddress);
        if (typeChanged) {
          this.buttons.splice(0);
          this.sensorType.fields.forEach((field) => {
            let defaultValue = field.type.default;
            if (this.defaultValues) {
              let val = this.defaultValues.find(x => x.conversion === field.type.conversion);
              if (val) {
                defaultValue = val.value;
              }
              val = this.defaultValues.find(x => x.fieldIndex === this.sensorType.fields.indexOf(field));
              if (val) {
                defaultValue = val.value;
              }
            }
            this.buttons.push({
              conversion: field.type.conversion,
              value: { value: field.type.default },
              initialValue: defaultValue,
              name: field.text ? field.text : field.type.text,
            });
          });
        }
      }
    },
  },
};
</script>

<style>

</style>
